import { AddOutlined, EditOutlined, RemoveCircleOutlined, WorkOutlineOutlined } from '@mui/icons-material';
import { Alert, Avatar, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { useEffect, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import type {
  ExternalCareer,
  ProfileCareerChangeInput,
  ProfileCareerChangeItem,
  ProfileChange,
  ProfileChangeDetail,
} from './model';
import { CompactEditButton, DefinitionList, EmptyState, LoadingIndicator, SectionCard, ValueBadge, designTokens } from '../../../design-system';
import {
  ExternalCareerChangeDialog,
  type ExternalCareerChangeSeed,
} from './ExternalCareerChangeDialog';
import type { CareerProfilePort } from './port';
import { ProfilePendingChanges } from './ProfilePendingChanges';
import { useAsyncResource } from '../../../shared/useAsyncResource';
import { profileDateValue, profileTextValue } from './profileDisplay';

export type ExternalCareerSectionProps = {
  port: CareerProfilePort;
  personId?: string;
  canRead: boolean;
  canWrite: boolean;
  canApprove?: boolean;
  fixtureMode: boolean;
  fixtureData?: ExternalCareer[];
};

type DialogState = {
  seed?: ExternalCareerChangeSeed;
};

const employmentTypeOptions = [
  { value: 'FULL_TIME', label: '정규직' },
  { value: 'PART_TIME', label: '파트타임' },
  { value: 'CONTRACT', label: '계약직' },
  { value: 'INTERN', label: '인턴' },
  { value: 'FREELANCE', label: '프리랜서' },
  { value: 'SELF_EMPLOYED', label: '자영업' },
  { value: 'OTHER', label: '기타' },
] as const;

const fixtureCareerData: ExternalCareer[] = [{
  id: 'fixture-career-1',
  employerName: '모빌리티랩',
  jobTitle: 'People Operations Manager',
  department: 'People Team',
  employmentType: 'FULL_TIME',
  startDate: '2020-03-01',
  endDate: '2023-02-28',
  description: '인사 운영 프로세스와 HR 시스템 도입을 담당했습니다.',
  displayOrder: 0,
}];

function employmentTypeLabel(value?: string) {
  return employmentTypeOptions.find((option) => option.value === value)?.label ?? value ?? '고용 형태 미등록';
}

const sectionInset = { xs: `${designTokens.layout.surfacePaddingCompact}px`, sm: `${designTokens.layout.surfacePadding}px` };

function actionLabel(action: string) {
  return ({ CREATE: '추가', UPDATE: '수정', END: '종료' } as Record<string, string>)[action] ?? action;
}

function operationKeys() {
  return { create: crypto.randomUUID(), submit: crypto.randomUUID() };
}

function upsertChange(changes: ProfileChange[], next: ProfileChange) {
  return [...changes.filter((change) => change.id !== next.id), next];
}

function setTransitioning(setter: Dispatch<SetStateAction<Set<string>>>, id: string, active: boolean) {
  setter((current) => {
    const next = new Set(current);
    if (active) next.add(id);
    else next.delete(id);
    return next;
  });
}

function stableKey(keys: Map<string, string>, id: string) {
  const existing = keys.get(id);
  if (existing) return existing;
  const created = crypto.randomUUID();
  keys.set(id, created);
  return created;
}

function detailCareers(detail: ProfileChangeDetail) {
  if (detail.details.careers?.length) return detail.details.careers;
  return detail.details.career ? [detail.details.career] : [];
}

export function ExternalCareerSection({
  port,
  personId,
  canRead,
  canWrite,
  canApprove = false,
  fixtureMode,
  fixtureData = fixtureCareerData,
}: ExternalCareerSectionProps) {
  const resource = useAsyncResource(
    async () => {
      if (!canRead) return { values: [], pending: [] };
      if (fixtureMode) return { values: fixtureData, pending: [] };
      const [career, changes] = await Promise.all([
        personId ? port.getPersonCareer(personId) : port.getMyCareer(),
        personId ? port.listPersonCareerChanges(personId) : port.listMyCareerChanges(),
      ]);
      return { values: career.data, pending: changes.data };
    },
    [canRead, fixtureData, fixtureMode, personId, port],
    '정보를 불러오지 못했습니다.',
  );
  const keys = useRef(operationKeys());
  const lastCreateFingerprint = useRef<string | undefined>(undefined);
  const submitKeys = useRef(new Map<string, string>());
  const approveKeys = useRef(new Map<string, string>());
  const localDetails = useRef(new Map<string, ProfileChangeDetail>());
  const [dialog, setDialog] = useState<DialogState>();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const [localPending, setLocalPending] = useState<ProfileChange[]>([]);
  const [submittingIds, setSubmittingIds] = useState<Set<string>>(new Set());
  const [approvingIds, setApprovingIds] = useState<Set<string>>(new Set());
  const [transitionErrors, setTransitionErrors] = useState<Record<string, string>>({});
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());
  const [approvedFeedback, setApprovedFeedback] = useState<ProfileChange>();

  useEffect(() => {
    if (fixtureMode || !resource.data) return;
    setLocalPending([]);
  }, [fixtureMode, resource.data]);

  useEffect(() => {
    if (!approvedFeedback) return;
    const timer = window.setTimeout(() => setApprovedFeedback(undefined), 5000);
    return () => window.clearTimeout(timer);
  }, [approvedFeedback]);

  if (!canRead) return null;

  const openDialog = (seed?: ExternalCareerChangeSeed) => {
    keys.current = operationKeys();
    lastCreateFingerprint.current = undefined;
    setError(undefined);
    setDialog({ seed });
  };

  const closeDialog = () => {
    if (busy) return;
    setDialog(undefined);
    setError(undefined);
  };

  const submitPending = (change: ProfileChange) => {
    if (change.status !== 'DRAFT' || !canWrite) return;
    setTransitioning(setSubmittingIds, change.id, true);
    setTransitionErrors((current) => {
      const next = { ...current };
      delete next[change.id];
      return next;
    });
    const request = fixtureMode
      ? Promise.resolve<ProfileChange>({ ...change, status: 'IN_PROGRESS', submittedAt: new Date().toISOString() })
      : personId
        ? port.submitPersonCareerChange(personId, change.id, stableKey(submitKeys.current, change.id))
        : port.submitCareerChange(change.id, stableKey(submitKeys.current, change.id));
    void request.then((submitted) => {
      setLocalPending((current) => upsertChange(current, submitted));
      resource.reload();
    }, (reason: unknown) => {
      setLocalPending((current) => upsertChange(current, { ...change, status: 'DRAFT' }));
      setTransitionErrors((current) => ({
        ...current,
        [change.id]: reason instanceof Error ? reason.message : '외부 경력 초안을 승인 요청으로 제출하지 못했습니다.',
      }));
    }).finally(() => setTransitioning(setSubmittingIds, change.id, false));
  };

  const approvePending = (change: ProfileChange) => {
    if (!personId || change.status !== 'IN_PROGRESS' || !canApprove) return;
    setTransitioning(setApprovingIds, change.id, true);
    setTransitionErrors((current) => {
      const next = { ...current };
      delete next[change.id];
      return next;
    });
    const request = fixtureMode
      ? Promise.resolve<ProfileChange>({ ...change, status: 'APPROVED', approvedAt: new Date().toISOString() })
      : port.approvePersonCareerChange(personId, change.id, stableKey(approveKeys.current, change.id));
    void request.then((approved) => {
      setLocalPending((current) => current.filter((item) => item.id !== approved.id));
      setResolvedIds((current) => new Set(current).add(approved.id));
      setApprovedFeedback(approved);
      resource.reload();
    }, (reason: unknown) => {
      setTransitionErrors((current) => ({
        ...current,
        [change.id]: reason instanceof Error ? reason.message : '외부 경력 변경 요청을 승인하지 못했습니다.',
      }));
    }).finally(() => setTransitioning(setApprovingIds, change.id, false));
  };

  const submit = (input: ProfileCareerChangeInput) => {
    const careers = input.careers;
    const firstCareer = careers[0];
    if (!input.effectiveDate || !firstCareer || careers.length > 20) {
      setError('효력 기준일과 1~20건의 외부 경력 변경을 확인해 주세요.');
      return;
    }
    const bundleCareers: [ProfileCareerChangeItem, ...ProfileCareerChangeItem[]] = [firstCareer, ...careers.slice(1)];
    const bundleInput: ProfileCareerChangeInput = { effectiveDate: input.effectiveDate, careers: bundleCareers };
    const fingerprint = JSON.stringify(bundleInput);
    if (lastCreateFingerprint.current && lastCreateFingerprint.current !== fingerprint) keys.current = operationKeys();
    lastCreateFingerprint.current = fingerprint;
    setBusy(true);
    setError(undefined);
    const create = fixtureMode
      ? Promise.resolve<ProfileChange>({ id: 'fixture-career-change', changeType: 'EXTERNAL_CAREER', status: 'DRAFT', effectiveDate: input.effectiveDate })
      : personId
        ? port.createPersonCareerChange(personId, bundleInput, keys.current.create)
        : port.createCareerChange(bundleInput, keys.current.create);
    void create.then((draft) => {
      submitKeys.current.set(draft.id, keys.current.submit);
      localDetails.current.set(draft.id, { ...draft, details: { careers } });
      setLocalPending((current) => upsertChange(current, draft));
      setTransitionErrors((current) => {
        const next = { ...current };
        delete next[draft.id];
        return next;
      });
      setBusy(false);
      setDialog(undefined);
      submitPending(draft);
    }, (reason: unknown) => {
      setError(reason instanceof Error ? reason.message : '외부 경력 변경 초안을 저장하지 못했습니다.');
      setBusy(false);
    });
  };

  const pendingById = new Map((resource.data?.pending ?? []).map((change) => [change.id, change]));
  for (const change of localPending) pendingById.set(change.id, change);
  const pending = Array.from(pendingById.values()).filter((change) => !resolvedIds.has(change.id) && (change.status === 'DRAFT' || change.status === 'IN_PROGRESS'));
  const values = resource.data?.values ?? [];
  const loadDetails = (change: ProfileChange) => {
    const local = localDetails.current.get(change.id);
    if (local) return Promise.resolve(local);
    return personId ? port.getPersonCareerChange(personId, change.id) : port.getMyCareerChange(change.id);
  };
  const renderDetails = (detail: ProfileChangeDetail) => {
    const changes = detailCareers(detail);
    if (!changes.length) return <Alert severity="warning">외부 경력 변경 내용을 확인할 수 없습니다.</Alert>;
    return <Stack spacing={1.25}>
      {changes.length > 1 && <Alert severity="info">이 요청에는 외부 경력 변경 {changes.length}건이 함께 포함되어 있습니다.</Alert>}
      {changes.map((career, index) => {
        const currentCareer = career.career?.id ? values.find((item) => item.id === career.career?.id) : undefined;
        const missingCurrentTarget = career.action !== 'CREATE' && !currentCareer;
        const target = currentCareer
          ? `${currentCareer.employerName} · ${currentCareer.jobTitle}`
          : career.career?.descriptor || (career.action === 'CREATE' ? '새 외부 경력' : '대상 경력 정보 없음');
        const items: Array<readonly [string, ReactNode]> = [
          ['변경 유형', <ValueBadge label={actionLabel(career.action)} />],
          ['대상', target],
        ];
        if (career.action !== 'END') items.push(
          ['회사명', profileTextValue(career.employerName) || '미입력'],
          ['직무/직책', profileTextValue(career.jobTitle) || '미입력'],
          ['부서', profileTextValue(career.department) || '미입력'],
          ['고용 형태', <ValueBadge label={employmentTypeLabel(career.employmentType)} />],
          ['근무 기간', `${profileDateValue(career.startDate) || '미입력'} ~ ${profileDateValue(career.endDate) || '현재'}`],
          ['주요 업무 및 성과', profileTextValue(career.description) || '미입력'],
        );
        return <Box key={`${career.action}-${career.career?.id ?? career.employerName ?? index}-${index}`} sx={{ p: 1.25, border: 1, borderColor: 'divider', borderRadius: designTokens.radius.surface }}>
          <Stack direction="row" spacing={0.75} sx={{ mb: 1, alignItems: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 720 }}>{changes.length > 1 ? `${index + 1}번째 경력 변경` : '경력 변경 내용'}</Typography>
          </Stack>
          <DefinitionList items={items} />
          {missingCurrentTarget && <Alert severity="warning" sx={{ mt: 1 }}>현재 원장에서 대상을 확인할 수 없어 처리할 수 없습니다.</Alert>}
        </Box>;
      })}
    </Stack>;
  };

  return <>
    <SectionCard
      title={<><WorkOutlineOutlined aria-hidden sx={{ mr: 1, fontSize: 20, color: 'text.secondary', verticalAlign: 'text-bottom' }} />외부 경력</>}
      action={canWrite && !resource.loading ? <Button size="small" variant="outlined" startIcon={<AddOutlined />} onClick={() => openDialog({ action: 'CREATE' })}>경력 추가하기</Button> : undefined}
      headerPadding="standard"
    >
      {approvedFeedback && <Box sx={{ px: sectionInset, pt: 1.5 }}>
        <Alert severity="success">
          <Typography variant="body2" sx={{ fontWeight: 720 }}>외부 경력 변경이 승인되어 원장에 반영됐습니다.</Typography>
          <Typography variant="caption">승인 완료</Typography>
        </Alert>
      </Box>}
      <ProfilePendingChanges
        changes={pending}
        label="외부 경력"
        canSubmit={canWrite}
        canApprove={Boolean(personId && canApprove)}
        submittingIds={submittingIds}
        approvingIds={approvingIds}
        errors={transitionErrors}
        loadDetails={loadDetails}
        renderDetails={renderDetails}
        isActionableDetail={(detail) => {
          const changes = detailCareers(detail);
          return changes.length > 0 && changes.every((career) => career.action === 'CREATE'
            || Boolean(career.career?.id && values.some((item) => item.id === career.career?.id)));
        }}
        onSubmit={submitPending}
        onApprove={approvePending}
      />
      {canWrite && !resource.loading && <Box sx={{ px: sectionInset, pt: 1.5 }}>
        <Button fullWidth variant="outlined" startIcon={<EditOutlined />} onClick={() => openDialog()}>
          경력 한 번에 수정
        </Button>
      </Box>}
      {resource.loading ? <Box sx={{ minHeight: 150, display: 'grid', placeItems: 'center' }}><LoadingIndicator label="외부 경력 불러오는 중" /></Box>
        : resource.error ? <Box sx={{ p: sectionInset }}><Alert severity="error" action={<Button color="inherit" size="small" onClick={resource.reload}>다시 시도</Button>}>{resource.error.message}</Alert></Box>
          : values.length ? <Stack divider={<Divider />} sx={{ mt: canWrite ? 1.5 : 0 }}>
            {values.map((career) => <Stack key={career.id} direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ px: sectionInset, py: 1.35, alignItems: { sm: 'center' } }}>
              <Stack direction="row" spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
                <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: designTokens.color.actionSoft, color: designTokens.color.action }}><WorkOutlineOutlined fontSize="small" /></Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Stack direction="row" spacing={0.75} sx={{ alignItems: 'center', flexWrap: 'wrap' }}><Typography variant="body2" sx={{ fontWeight: 720 }}>{career.employerName}</Typography><ValueBadge label={employmentTypeLabel(career.employmentType)} /></Stack>
                  <Typography variant="caption" component="div" color="text.secondary">{career.jobTitle}{career.department ? ` · ${career.department}` : ''}</Typography>
                  <Typography variant="caption" component="div" color="text.secondary">{profileDateValue(career.startDate) || '미입력'} ~ {profileDateValue(career.endDate) || '현재'}</Typography>
                  {career.description && <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>{career.description}</Typography>}
                </Box>
              </Stack>
              {canWrite && <Stack direction="row" spacing={0.5}>
                <CompactEditButton aria-label={`${career.employerName} 외부 경력 수정`} onClick={() => openDialog({ action: 'UPDATE', career })}>수정</CompactEditButton>
                <Button size="small" color="error" startIcon={<RemoveCircleOutlined />} aria-label={`${career.employerName} 외부 경력 종료`} onClick={() => openDialog({ action: 'END', career })}>종료</Button>
              </Stack>}
            </Stack>)}
          </Stack>
            : <EmptyState>등록된 외부 경력이 없습니다.</EmptyState>}
    </SectionCard>

    <ExternalCareerChangeDialog
      open={Boolean(dialog)}
      careers={values}
      seed={dialog?.seed}
      busy={busy}
      error={error}
      onClose={closeDialog}
      onSubmit={submit}
    />
  </>;
}
